import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

type Step = { id: string; text: string; isOptional: boolean };

interface HorizontalLinearStepperProps {
  steps: Step[];
  currentStepIndex: number;
}

export const HorizontalLinearStepper = ({
  steps,
  currentStepIndex,
}: HorizontalLinearStepperProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());

  const isStepOptional = (index: number) => {
    return steps[index].isOptional;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  useEffect(() => {
    setActiveStep(currentStepIndex);
  }, [currentStepIndex, steps]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper
        activeStep={activeStep}
        sx={{
          '& .MuiSvgIcon-root.Mui-active, & .MuiSvgIcon-root.Mui-completed': {
            color: '#1F7983',
          },
          '& .MuiStepLabel-label': {
            fontFamily: 'Tajawal',
          },
        }}
      >
        {steps.map((step, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          if (isStepOptional(index)) {
            labelProps.optional = (
              <Typography variant="caption">Optional</Typography>
            );
          }
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={step.id} {...stepProps}>
              <StepLabel {...labelProps}>
                <ReturnTranslatedText
                  origintext={step.text}
                  text={`general.${step.text}`}
                />
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};
